import { useEffect, useRef } from 'react';

/**
 * Scroll-position-based load-more for item pagination.
 *
 * Attaches a passive scroll listener to the element returned by
 * `getScrollElement`. Fires `onLoadMore` when the user scrolls downward
 * and is within `threshold` px of the bottom.
 *
 * Skips when `isOpen` is false (section collapsed), `hasNextPage` is
 * false, or `isLoading` is true.
 */
export function useScrollLoadMore({
  onLoadMore,
  hasNextPage,
  isLoading,
  isOpen,
  getScrollElement,
  threshold = 100,
}: {
  onLoadMore: () => void;
  hasNextPage: boolean;
  isLoading: boolean;
  isOpen: boolean;
  getScrollElement: () => HTMLElement | null;
  threshold?: number;
}) {
  const onLoadMoreRef = useRef(onLoadMore);
  onLoadMoreRef.current = onLoadMore;
  const prevScrollTopRef = useRef(0);
  const isOpenRef = useRef(isOpen);
  isOpenRef.current = isOpen;

  useEffect(() => {
    const el = getScrollElement();
    if (!el) return;
    prevScrollTopRef.current = el.scrollTop;

    const handleScroll = () => {
      if (!isOpenRef.current) return;
      if (!hasNextPage || isLoading) return;
      const { scrollTop, scrollHeight, clientHeight } = el;
      const isScrollingDown = scrollTop > prevScrollTopRef.current;
      prevScrollTopRef.current = scrollTop;
      if (!isScrollingDown) return;
      if (scrollHeight - scrollTop - clientHeight < threshold) {
        onLoadMoreRef.current();
      }
    };

    el.addEventListener('scroll', handleScroll, { passive: true });
    return () => el.removeEventListener('scroll', handleScroll);
  }, [hasNextPage, isLoading, getScrollElement, threshold]);
}
